/**
 * Custom hook for managing hint system
 * Handles hint button clicks, hint tutorial modal, and stuck timer
 * Optimized with pre-computation using requestIdleCallback
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { GameState, findShortestPathAndNextWordForGameState } from '../WordDriftUtils';
import { SaveDataOrchestrator, logger } from '@mental-mint/shared-utils';
import { Game, playSound, SharedSoundEffect } from '@mental-mint/shared-ui';
import { GameId } from '@mental-mint/shared-types';
import { WordDriftSaveData } from '../types/SaveData';

// Full BFS data for a single word (distance + lemmatized form)
export interface BfsWordData {
  word: string;           // Clean display word (e.g., "plant")
  lemmatized: string;     // Lemmatized form (e.g., "plant")
  distance: number | null; // BFS distance to goal, null if unreachable
}

// Complete pre-computed BFS data for all clickable words on a page
export interface PreComputedBfsData {
  wordDistances: BfsWordData[];  // All clickable words with their distances
  bestWord: string | null;        // Display form of best next word
  bestWordLemmatized: string | null; // Lemmatized form of best next word
  bestDistance: number | null;    // Distance of best word
  wordsToGrayOut: string[];       // Words to gray out for hint (worst half)
  wordLemma: string;              // The current word this data is for
}

interface UseHintSystemReturn {
  isHintInProgress: boolean;
  showHintTutorial: boolean;
  handleHintClick: () => Promise<void>;
  handleAcceptHintTutorial: (dontShowAgain: boolean) => void;
  handleDeclineHintTutorial: (dontShowAgain: boolean) => void;
  isHintDisabled: boolean;
  getBfsData: () => PreComputedBfsData | null;  // Get cached BFS data for current word
  getClickedWordDistance: (clickedWordLemma: string) => number | null; // Get distance for a clicked word
}

export function useHintSystem(
  gameState: GameState | null,
  setGameState: React.Dispatch<React.SetStateAction<GameState | null>>,
  grayedOutWordsPerPageRef: React.MutableRefObject<Map<string, Set<string>>>,
  isTimerActive: boolean,
  deductTime: (seconds: number) => void,
  game: Game
): UseHintSystemReturn {
  const [isHintInProgress, setIsHintInProgress] = useState(false);
  const [showHintTutorial, setShowHintTutorial] = useState(false);
  
  // Cache for pre-computed BFS data per word (shared by hints AND move feedback)
  const preComputedBfsRef = useRef<Map<string, PreComputedBfsData>>(new Map());
  const preComputeInProgressRef = useRef<Set<string>>(new Set());

  // Calculate current hint penalty based on hints used (starts at 30s, increases by 15s each time)
  const currentHintPenalty = gameState ? 30 + (15 * gameState.hintsUsed) : 30;

  // Check if hint button should be disabled
  const isHintDisabled = !gameState || gameState.isComplete || isHintInProgress || gameState.hintUsedForCurrentWord;

  // Pre-compute BFS data when word loads (runs in background during idle time)
  // This shared cache is used by BOTH hints AND move feedback to eliminate UI freezing
  useEffect(() => {
    if (!gameState || gameState.isComplete) return;

    const wordLemma = gameState.currentWordPlayableInfo.word.variations.lemmatized;
    
    // Skip if already computed or in progress
    if (preComputedBfsRef.current.has(wordLemma) || preComputeInProgressRef.current.has(wordLemma)) {
      return;
    }

    // DELAY: Wait for animations to complete before starting BFS computation
    // Page turn + move feedback animations take ~800ms, so wait 1 second to be safe
    const animationDelay = setTimeout(() => {
      // Mark as in progress after animation delay
      preComputeInProgressRef.current.add(wordLemma);

      const computeBfsData = async () => {
        const wordInfo = gameState.currentWordPlayableInfo;
        
        // Get ONLY the clickable words (already limited by limitWordsForDisplay to ~4 words)
        // These are the words that actually appear in the UI and can be clicked
        const clickableWords = wordInfo.clickableWords;

        if (clickableWords.length === 0) {
          preComputeInProgressRef.current.delete(wordLemma);
          return;
        }

        const currentDistance = gameState.currentPathDistanceToGoal;
        if (currentDistance === null || currentDistance === undefined) {
          preComputeInProgressRef.current.delete(wordLemma);
          return;
        }

        logger.debug(`🔄 Pre-computing BFS data for "${wordLemma}" (${clickableWords.length} clickable words)`);

        // CRITICAL FIX: Process words ONE AT A TIME with delays between them
        // This prevents blocking the timer updates and makes the UI responsive
        const wordDistances: BfsWordData[] = [];
        let bestWord: string | null = null;
        let bestWordLemmatized: string | null = null;
        let bestDistance: number | null = null;

        for (let i = 0; i < clickableWords.length; i++) {
          const cleanWord = clickableWords[i];
          const gameWord = wordInfo.wordLemmaMap[cleanWord];
          if (!gameWord) continue;

          // Process this word
          // Use BFS instead of A* for pre-computation (much faster, acceptable accuracy for hints)
          const { distance } = findShortestPathAndNextWordForGameState(
            gameWord.variations.lemmatized,
            gameState.goalWord,
            20,
            false // Use BFS for performance
          );

          wordDistances.push({ 
            word: cleanWord, 
            lemmatized: gameWord.variations.lemmatized,
            distance 
          });

          // Track the best word (lowest distance)
          if (distance !== null && (bestDistance === null || distance < bestDistance)) {
            bestDistance = distance;
            bestWord = cleanWord;
            bestWordLemmatized = gameWord.variations.lemmatized;
          }

          // Yield to browser between words to prevent blocking timer/animations
          // This allows timer to update and keeps UI responsive
          if (i < clickableWords.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }

        // Sort by helpfulness (lower distance = more helpful, worst first for graying)
        const sortedForGraying = [...wordDistances].sort((a, b) => {
          const distA = a.distance === null ? Infinity : a.distance;
          const distB = b.distance === null ? Infinity : b.distance;
          return distB - distA; // Sort descending (worst first)
        });

        // Gray out the worst half (for hint system)
        const numToGrayOut = Math.ceil(sortedForGraying.length / 2);
        const wordsToGrayOut = sortedForGraying.slice(0, numToGrayOut).map(w => w.word);

        // Cache the complete BFS data
        const bfsData: PreComputedBfsData = {
          wordDistances,
          bestWord,
          bestWordLemmatized,
          bestDistance,
          wordsToGrayOut,
          wordLemma
        };
        
        preComputedBfsRef.current.set(wordLemma, bfsData);
        preComputeInProgressRef.current.delete(wordLemma);
        
        // Cache cleanup: Keep only last 20 entries when cache grows beyond 30
        if (preComputedBfsRef.current.size > 30) {
          const entries = Array.from(preComputedBfsRef.current.entries());
          const entriesToKeep = entries.slice(-20); // Keep last 20
          preComputedBfsRef.current.clear();
          entriesToKeep.forEach(([key, value]) => preComputedBfsRef.current.set(key, value));
          logger.debug(`🧹 Cleaned BFS cache (kept last 20 of ${entries.length} entries)`);
        }
        
        logger.debug(`✅ Pre-computed BFS data for "${wordLemma}" - best: ${bestWord} (${bestDistance}), gray out ${numToGrayOut}/${wordDistances.length}`);
      };

      // Start the async computation (doesn't block)
      computeBfsData();
    }, 1000); // Wait 1 second for animations to complete

    return () => clearTimeout(animationDelay);
  }, [gameState?.currentWordPlayableInfo.word.variations.lemmatized, gameState?.isComplete, gameState?.goalWord, gameState?.currentPathDistanceToGoal]);

  // Show hint tutorial modal after 60 seconds stuck (using simplified setTimeout approach)
  useEffect(() => {
    if (!gameState || gameState.isComplete || !isTimerActive || gameState.hintUsedForCurrentWord || !gameState.optimalPath) {
      return;
    }

    const timer = setTimeout(async () => {
      // Check if should show tutorial (not permanently dismissed)
      const saveData = await SaveDataOrchestrator.loadGameData<WordDriftSaveData>(GameId.Taptionary);
      if (!saveData?.savedGameData?.hintTutorialPermanentlyDismissed) {
        setShowHintTutorial(true);
      }
    }, 60000); // 60 seconds (1 minute)

    return () => clearTimeout(timer);
  }, [gameState?.path, isTimerActive, gameState?.isComplete, gameState?.hintUsedForCurrentWord, gameState?.optimalPath, game]);

  // Handle hint button click - use pre-computed data if available, otherwise compute on-demand
  const handleHintClick = useCallback(async () => {
    if (!gameState || gameState.isComplete || isHintInProgress) return;

    // Don't allow hint if already used for current word
    if (gameState.hintUsedForCurrentWord) {
      logger.debug("Hint already used for current word");
      return;
    }

    const wordInfo = gameState.currentWordPlayableInfo;
    const wordLemma = wordInfo.word.variations.lemmatized;

    // Update game state to mark hint as used and increment counter
    const updatedGameState: GameState = {
      ...gameState,
      hintUsedForCurrentWord: true,
      hintsUsed: gameState.hintsUsed + 1,
    };
    setGameState(updatedGameState);
    
    // Deduct time using the game timer hook (penalty display handled by TopNav)
    deductTime(currentHintPenalty);
    
    // Play hint sound
    playSound(SharedSoundEffect.HINT);

    // Check if we have pre-computed BFS data (should be instant!)
    const preComputedData = preComputedBfsRef.current.get(wordLemma);
    
    if (preComputedData) {
      // Use pre-computed data - no UI freeze!
      logger.debug(`✨ Using pre-computed BFS data for hint "${wordLemma}" (instant!)`);
      
      const existingGrayedWords = grayedOutWordsPerPageRef.current.get(wordLemma) || new Set<string>();
      const newSet = new Set([...existingGrayedWords, ...preComputedData.wordsToGrayOut]);
      grayedOutWordsPerPageRef.current.set(wordLemma, newSet);
      
      return;
    }

    // No pre-computed data - compute on-demand (but still only for clickable words!)
    logger.debug(`⚡ Computing BFS on-demand for hint "${wordLemma}" (pre-compute didn't finish)`);
    setIsHintInProgress(true);

    try {
      // Get ONLY clickable words (already limited to ~4 words by limitWordsForDisplay)
      const clickableWords = wordInfo.clickableWords;

      if (clickableWords.length === 0) {
        logger.debug("No clickable words to analyze for hint");
        return;
      }

      const currentDistance = gameState.currentPathDistanceToGoal;
      if (currentDistance === null || currentDistance === undefined) {
        logger.debug("No path distance available, cannot determine helpful vs unhelpful words");
        return;
      }

      logger.debug(`Computing hint for ${clickableWords.length} clickable words`);

      // Calculate BFS for clickable words only
      const wordDistances: Array<{ word: string; distance: number | null }> = [];

      for (const cleanWord of clickableWords) {
        const gameWord = wordInfo.wordLemmaMap[cleanWord];
        if (!gameWord) continue;

        const { distance } = findShortestPathAndNextWordForGameState(
          gameWord.variations.lemmatized,
          gameState.goalWord
        );

        wordDistances.push({ word: cleanWord, distance });
      }

      // Sort by helpfulness
      wordDistances.sort((a, b) => {
        const distA = a.distance === null ? Infinity : a.distance;
        const distB = b.distance === null ? Infinity : b.distance;
        return distB - distA;
      });

      // Gray out the worst half
      const numToGrayOut = Math.ceil(wordDistances.length / 2);
      const wordsToGrayOut = wordDistances.slice(0, numToGrayOut).map(w => w.word);

      logger.debug(`Graying out ${numToGrayOut}/${wordDistances.length} words`);

      // Save grayed out words to per-page cache
      const existingGrayedWords = grayedOutWordsPerPageRef.current.get(wordLemma) || new Set<string>();
      const newSet = new Set([...existingGrayedWords, ...wordsToGrayOut]);
      grayedOutWordsPerPageRef.current.set(wordLemma, newSet);
    } finally {
      // CRITICAL: Always reset isHintInProgress, even if early return or error
      setIsHintInProgress(false);
    }
  }, [gameState, isHintInProgress, currentHintPenalty, setGameState, grayedOutWordsPerPageRef, deductTime]);

  // Helper to save permanent dismissal of hint tutorial
  const savePermanentDismissal = useCallback(async () => {
    const saveData = await SaveDataOrchestrator.loadGameData<WordDriftSaveData>(GameId.Taptionary);
    if (saveData?.savedGameData) {
      await SaveDataOrchestrator.saveGameData(GameId.Taptionary, {
        ...saveData.savedGameData,
        hintTutorialPermanentlyDismissed: true
      });
    }
  }, []);

  // Handle accepting hint from tutorial modal
  const handleAcceptHintTutorial = useCallback(async (dontShowAgain: boolean) => {
    setShowHintTutorial(false);

    if (dontShowAgain) {
      await savePermanentDismissal();
    }

    // Trigger the hint
    handleHintClick();
  }, [savePermanentDismissal, handleHintClick]);

  // Handle declining hint from tutorial modal
  const handleDeclineHintTutorial = useCallback(async (dontShowAgain: boolean) => {
    setShowHintTutorial(false);

    if (dontShowAgain) {
      await savePermanentDismissal();
    }
  }, [savePermanentDismissal]);

  // Get cached BFS data for the current word (used by move feedback)
  const getBfsData = useCallback((): PreComputedBfsData | null => {
    if (!gameState) return null;
    const wordLemma = gameState.currentWordPlayableInfo.word.variations.lemmatized;
    return preComputedBfsRef.current.get(wordLemma) || null;
  }, [gameState]);

  // Get distance for a clicked word from cached BFS data
  const getClickedWordDistance = useCallback((clickedWordLemma: string): number | null => {
    if (!gameState) return null;
    const wordLemma = gameState.currentWordPlayableInfo.word.variations.lemmatized;
    const bfsData = preComputedBfsRef.current.get(wordLemma);
    
    if (!bfsData) return null;
    
    // Find the clicked word in our pre-computed distances
    const wordData = bfsData.wordDistances.find(w => w.lemmatized === clickedWordLemma);
    return wordData?.distance || null;
  }, [gameState]);

  return {
    isHintInProgress,
    showHintTutorial,
    handleHintClick,
    handleAcceptHintTutorial,
    handleDeclineHintTutorial,
    isHintDisabled,
    getBfsData,
    getClickedWordDistance,
  };
}
